import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { useMutation } from "@tanstack/react-query";
import { OtcRequestItem, OtcRequestStatus } from "../types";
import { AttachmentsEmptyState } from "../components/AttachmentsEmptyState";
import { SendOfferModal, SendOfferSubmitData } from "../components/SendOfferModal";
import { EscalateModal } from "../components/EscalateModal";
import { RejectModal } from "../components/RejectModal";
import { ApproveRequestModal } from "../components/ApproveRequestModal";
import { UpdateStatusModal, UpdateStatusSubmitData } from "../components/UpdateStatusModal";
import { CancelOfferModal } from "../components/CancelOfferModal";
import { OtcRequestTopSection } from "../components/OtcRequestTopSection";
import { OtcRequestOverviewCard } from "../components/OtcRequestOverviewCard";
import { Card, CardContent, HeadingText } from "../../../shared/ui";
import { getApiErrorMessage } from "../../../shared/lib";

import {
  ApproveOfferPayload,
  SendOfferPayload,
  UpdateTransactionStatusPayload,
  approveOtcOffer,
  cancelOtcRequest,
  escalateOtcRequest,
  rejectOtcRequest,
  sendOtcOffer,
  updateOtcTransactionStatus,
  uploadReceiptFile,
} from "../services";

const INITIAL_OTC_REQUESTS: OtcRequestItem[] = [];

export const OtcRequestDetailsPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  // Local state initialized with mock data
  const [requests, setRequests] = useState<OtcRequestItem[]>(INITIAL_OTC_REQUESTS);

  // Modals
  const [isSendOfferOpen, setIsSendOfferOpen] = useState(false);
  const [isEscalateOpen, setIsEscalateOpen] = useState(false);
  const [isRejectOpen, setIsRejectOpen] = useState(false);
  const [isApproveOpen, setIsApproveOpen] = useState(false);
  const [isUpdateStatusOpen, setIsUpdateStatusOpen] = useState(false);
  const [isCancelOfferOpen, setIsCancelOfferOpen] = useState(false);

  // Current request index and item
  const currentIndex = requests.findIndex(
    (item) => item.id === id || item.transactionId.includes(id || "")
  );
  const currentRequest: OtcRequestItem =
    currentIndex !== -1 ? requests[currentIndex] : requests[0];

  const canGoPrevious = currentIndex > 0;
  const canGoNext = currentIndex < requests.length - 1;

  const handleNavigateSibling = (delta: number) => {
    const nextIdx = currentIndex + delta;
    if (nextIdx >= 0 && nextIdx < requests.length) {
      window.location.hash = `#/otc-requests/${requests[nextIdx].id}`;
    }
  };

  const sendOfferMutation = useMutation({
    mutationFn: async (payload: SendOfferPayload) => {
      const targetId = currentRequest.id || currentRequest.transactionId;
      return sendOtcOffer(targetId, payload);
    },
    onSuccess: (_, variables) => {
      const nowStr = new Date().toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });

      setRequests((prev) =>
        prev.map((item) => {
          if (item.id === currentRequest.id) {
            return {
              ...item,
              state: "Offer sent",
              offeredRate: `₦${variables.offeredRate.toLocaleString()} / USD`,
              offeredRateValue: variables.offeredRate,
              settlementMethod: variables.providerId || variables.settlementMethod,
              expiringIn: `Valid for ${variables.expiresInMinutes} minutes`,
              timeline: [
                {
                  id: `t-${Date.now()}`,
                  title: "Offer sent",
                  timestamp: `${nowStr} • Just now`,
                  completed: true,
                },
                ...item.timeline,
              ],
              activityLog: [
                {
                  id: `a-${Date.now()}`,
                  timestamp: `${nowStr} • Just now`,
                  user: "Admin",
                  action: `Sent offer at ₦${variables.offeredRate.toLocaleString()} / USD via ${variables.providerId || variables.settlementMethod}`,
                  icon: "offer",
                },
                ...item.activityLog,
              ],
            };
          }
          return item;
        })
      );
      setIsSendOfferOpen(false);
      toast.success("Offer sent to customer successfully");
    },
    onError: (err) => {
      toast.error(getApiErrorMessage(err, "Failed to send counter-offer"));
    },
  });

  const approveOfferMutation = useMutation({
    mutationFn: async (payload: ApproveOfferPayload) => {
      const targetId = currentRequest.id || currentRequest.transactionId;
      return approveOtcOffer(targetId, payload);
    },
    onSuccess: (_, variables) => {
      const nowStr = new Date().toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });

      setRequests((prev) =>
        prev.map((item) => {
          if (item.id === currentRequest.id) {
            return {
              ...item,
              state: "Request approved",
              settlementMethod: variables.providerId || variables.settlementMethod,
              expiringIn: `Valid for ${variables.expiresInMinutes} minutes`,
              timeline: [
                {
                  id: `t-${Date.now()}`,
                  title: "Request approved",
                  timestamp: `${nowStr} • Just now`,
                  completed: true,
                },
                ...item.timeline,
              ],
              activityLog: [
                {
                  id: `a-${Date.now()}`,
                  timestamp: `${nowStr} • Just now`,
                  user: "Admin",
                  action: `Approved request via ${variables.providerId || variables.settlementMethod}`,
                  icon: "wallet",
                },
                ...item.activityLog,
              ],
            };
          }
          return item;
        })
      );
      setIsApproveOpen(false);
      toast.success("OTC request approved successfully");
    },
    onError: (err) => {
      toast.error(getApiErrorMessage(err, "Failed to approve OTC request"));
    },
  });

  const cancelOfferMutation = useMutation({
    mutationFn: async () => {
      const targetId = currentRequest.id || currentRequest.transactionId;
      return cancelOtcRequest(targetId, "Offer cancelled by admin");
    },
    onSuccess: () => {
      setRequests((prev) =>
        prev.map((item) => {
          if (item.id === currentRequest.id) {
            return {
              ...item,
              state: "Under review",
              timeline: item.timeline.filter((t) => t.title !== "Offer sent"),
              activityLog: [
                {
                  id: `a-${Date.now()}`,
                  timestamp: "Just now",
                  user: "Admin",
                  action: "Cancelled offer",
                  icon: "status",
                },
                ...item.activityLog,
              ],
            };
          }
          return item;
        })
      );
      setIsCancelOfferOpen(false);
      toast.success("Offer cancelled successfully");
    },
    onError: (err) => {
      toast.error(getApiErrorMessage(err, "Failed to cancel offer"));
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: async (payload: UpdateStatusSubmitData) => {
      const targetId = currentRequest.id || currentRequest.transactionId;

      let receiptUrl: string | undefined = undefined;
      if (payload.status === "Completed" && payload.receiptFile) {
        receiptUrl = await uploadReceiptFile(payload.receiptFile);
      }

      const txPayload: UpdateTransactionStatusPayload =
        payload.status === "Completed"
          ? receiptUrl
            ? { status: "Success", url: receiptUrl }
            : { status: "Success" }
          : payload.status === "Failed"
            ? {
                status: "Failed",
                reason: payload.reason || "Settlement could not be completed.",
              }
            : { status: "Processing" };

      return updateOtcTransactionStatus(targetId, txPayload);
    },
    onSuccess: (_, variables) => {
      const displayStatus: OtcRequestStatus =
        variables.status === "Completed"
          ? "Completed"
          : variables.status === "Failed"
            ? "Rejected"
            : "Processing";

      setRequests((prev) =>
        prev.map((item) => {
          if (item.id === currentRequest.id) {
            return {
              ...item,
              state: displayStatus,
              activityLog: [
                {
                  id: `a-${Date.now()}`,
                  timestamp: "Just now",
                  user: "Admin",
                  action: `Updated transaction status to ${variables.status}${
                    variables.reason ? `: ${variables.reason}` : ""
                  }`,
                  icon: "status",
                },
                ...item.activityLog,
              ],
            };
          }
          return item;
        })
      );
      setIsUpdateStatusOpen(false);
      toast.success(`Transaction status updated to ${variables.status}`);
    },
    onError: (err, variables) => {
      toast.error(
        getApiErrorMessage(
          err,
          `Failed to update transaction status to ${variables.status}`
        )
      );
    },
  });

  const escalateMutation = useMutation({
    mutationFn: async (payload: {
      adminId: string;
      role: string;
      note: string;
    }) => {
      const targetId = currentRequest.id || currentRequest.transactionId;
      return escalateOtcRequest(targetId, {
        adminId: payload.adminId,
        role: payload.role,
        note: payload.note,
      });
    },
    onSuccess: (_, variables) => {
      setRequests((prev) =>
        prev.map((item) => {
          if (item.id === currentRequest.id) {
            return {
              ...item,
              activityLog: [
                {
                  id: `a-${Date.now()}`,
                  timestamp: "Just now",
                  user: "Admin",
                  action: `Escalated request to ${variables.role}: ${variables.note}`,
                  icon: "escalate",
                },
                ...item.activityLog,
              ],
            };
          }
          return item;
        })
      );
      setIsEscalateOpen(false);
      toast.success(`Request escalated to ${variables.role}`);
    },
    onError: (err) => {
      toast.error(getApiErrorMessage(err, "Failed to escalate request"));
    },
  });

  const rejectMutation = useMutation({
    mutationFn: async (reason: string) => {
      const targetId = currentRequest.id || currentRequest.transactionId;
      return rejectOtcRequest(targetId, reason);
    },
    onSuccess: (_, reason) => {
      setRequests((prev) =>
        prev.map((item) => {
          if (item.id === currentRequest.id) {
            return {
              ...item,
              state: "Rejected",
              activityLog: [
                {
                  id: `a-${Date.now()}`,
                  timestamp: "Just now",
                  user: "Admin",
                  action: `Rejected request: ${reason}`,
                  icon: "status",
                },
                ...item.activityLog,
              ],
            };
          }
          return item;
        })
      );
      setIsRejectOpen(false);
      toast.success("OTC request rejected successfully");
    },
    onError: (err) => {
      toast.error(getApiErrorMessage(err, "Failed to reject OTC request"));
    },
  });

  const isActionSubmitting =
    sendOfferMutation.isPending ||
    approveOfferMutation.isPending ||
    cancelOfferMutation.isPending ||
    updateStatusMutation.isPending ||
    escalateMutation.isPending ||
    rejectMutation.isPending;

  const handleSendOfferSubmit = (offerData: SendOfferSubmitData) => {
    sendOfferMutation.mutate(offerData);
  };

  const handleApproveConfirm = (
    providerId: string,
    _note?: string,
    validity?: {
      validityInMinutes?: number;
      [key: string]: any;
    }
  ) => {
    const minutes = validity?.validityInMinutes ?? 30;

    approveOfferMutation.mutate({
      acceptRequestedRate: true,
      providerId: providerId,
      expiresInMinutes: minutes,
    });
  };

  const handleCancelOffer = async () => {
    cancelOfferMutation.mutate();
  };

  const handleUpdateStatusConfirm = async (data: UpdateStatusSubmitData) => {
    updateStatusMutation.mutate(data);
  };

  const handleEscalateConfirm = async (data: {
    adminId: string;
    role: string;
    note: string;
  }) => {
    escalateMutation.mutate(data);
  };

  const handleRejectConfirm = async (reason: string) => {
    rejectMutation.mutate(reason);
  };

  return (
    <section className="min-h-full bg-surface-page md:p-6">
      <div className="rounded-[16px] bg-surface-card p-4 md:p-6">
        {/* Top Section with Back, pagination, and state action buttons */}
        <OtcRequestTopSection
          request={currentRequest}
          canGoPrevious={canGoPrevious}
          canGoNext={canGoNext}
          onNavigateSibling={handleNavigateSibling}
          onReject={() => setIsRejectOpen(true)}
          onSendOffer={() => setIsSendOfferOpen(true)}
          onApprove={() => setIsApproveOpen(true)}
          onCancelOffer={() => setIsCancelOfferOpen(true)}
          onUpdateStatus={() => setIsUpdateStatusOpen(true)}
          onEscalate={() => setIsEscalateOpen(true)}
          onCompleteTransfer={() => {
            const targetId = currentRequest.id || currentRequest.transactionId;
            navigate(`/otc-requests/${targetId}/complete-transfer`, {
              state: { request: currentRequest },
            });
          }}
        />

        {/* Main 3-column Overview Card */}
        <OtcRequestOverviewCard request={currentRequest} />

        {/* Bottom Section: Attachments & Activity Log Cards */}
        <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-2">
          {/* Attachments Card */}
          <Card className="overflow-hidden rounded-[16px] border border-primary-80 bg-white shadow-none">
            <CardContent className="p-6">
              <HeadingText className="aku-body-small-medium font-semibold text-gray-30">
                Attachments
              </HeadingText>
              <AttachmentsEmptyState />
            </CardContent>
          </Card>

          {/* Activity Log Card */}
          <Card className="overflow-hidden rounded-[16px] border border-primary-80 bg-white shadow-none">
            <CardContent className="p-6">
              <HeadingText className="aku-body-small-medium font-semibold text-gray-30">
                Activity Log
              </HeadingText>

              <div className="mt-6 relative space-y-6 pl-5 before:absolute before:bottom-3 before:left-[7px] before:top-2 before:w-0.5 before:bg-primary-80">
                {currentRequest.activityLog.map((log) => (
                  <div key={log.id} className="relative">
                    <div
                      className={`absolute -left-5 top-1 flex h-3.5 w-3.5 items-center justify-center rounded-full ring-4 ring-purple-50 ${
                        log.icon === "wallet"
                          ? "bg-brand"
                          : log.icon === "offer"
                          ? "bg-amber-500"
                          : "bg-orange-500"
                      }`}
                    >
                      <div className="h-1.5 w-1.5 rounded-full bg-white" />
                    </div>
                    <p className="aku-label-regular text-gray-70">
                      {log.timestamp}
                    </p>
                    <p className="mt-1 aku-body-small-regular text-neutral-700">
                      <span className="font-semibold text-gray-30">
                        {log.user}
                      </span>{" "}
                      {log.action}{" "}
                      {log.target && (
                        <span className="font-semibold text-gray-30">
                          {log.target}
                        </span>
                      )}
                      {log.fromValue && (
                        <>
                          {" "}from{" "}
                          <span className="font-semibold text-gray-30">
                            {log.fromValue}
                          </span>{" "}
                          to{" "}
                          <span className="font-semibold text-gray-30">
                            {log.toValue}
                          </span>
                        </>
                      )}
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Modals aligned with design system */}
        <SendOfferModal
          open={isSendOfferOpen}
          request={currentRequest}
          isSubmitting={isActionSubmitting}
          onClose={() => setIsSendOfferOpen(false)}
          onSubmit={handleSendOfferSubmit}
        />

        <ApproveRequestModal
          open={isApproveOpen}
          request={currentRequest}
          isSubmitting={isActionSubmitting}
          onClose={() => setIsApproveOpen(false)}
          onConfirm={handleApproveConfirm}
          onSendCounterOffer={() => {
            setIsApproveOpen(false);
            setIsSendOfferOpen(true);
          }}
        />

        <EscalateModal
          open={isEscalateOpen}
          customerName={currentRequest.customer.name}
          isSubmitting={isActionSubmitting}
          onClose={() => setIsEscalateOpen(false)}
          onConfirm={handleEscalateConfirm}
        />

        <RejectModal
          open={isRejectOpen}
          customerName={currentRequest.customer.name}
          isSubmitting={isActionSubmitting}
          onClose={() => setIsRejectOpen(false)}
          onConfirm={handleRejectConfirm}
        />

        <UpdateStatusModal
          open={isUpdateStatusOpen}
          currentStatus={currentRequest.state}
          isSubmitting={isActionSubmitting}
          onClose={() => setIsUpdateStatusOpen(false)}
          onConfirm={handleUpdateStatusConfirm}
        />

        <CancelOfferModal
          open={isCancelOfferOpen}
          isSubmitting={cancelOfferMutation.isPending}
          onClose={() => setIsCancelOfferOpen(false)}
          onConfirm={handleCancelOffer}
        />
      </div>
    </section>
  );
};

export default OtcRequestDetailsPage;
